import { useCallback, useContext, memo } from 'react';
import styled from '@emotion/styled';
import type { Chat } from '@trinity/chat';
import { ChatHintType, ChatResponseType } from '@trinity/chat';
import { CapabilitiesContext } from '@trinity/chat-components/components/BrandInsights/context';
import { ChatResponsePane } from '@trinity/chat-components/components/Chat/ResponsePane';
import { OpenQueryFeedbackModalContext } from '@trinity/chat-components/components/Chat.Feedback/context.js';
import {
  StarRating,
  useUpdateQueryRatingMutation,
} from '@trinity/chat-components/components/Chat.Feedback/index.js';
import { useShareChatModal } from '@trinity/chat-components/components/Chat.History/Modal.ShareChat.js';
import { MessageCitations } from '@trinity/chat-components/components/Chat.Message.Citations/index.js';
import { useChatApiClient, useGroupFeatures } from '@trinity/chat-components/containers/Chat';
import { useCopyToClipboard } from '@trinity/chat-components/hooks/useCopyToClipboard';
import { ActiveChatSessionContext, MessageCitationsToggleContext } from './context';
import { useChatPermissions } from './hooks/useChatPermissions';
import { MessageButtons } from './Message.Buttons';
import { CopyButton } from './Message.CopyToClipboard';
import { DownloadChartsButton } from './Message.Download.Chart';
import { GoToTraceUrl } from './Message.GoToTraceUrl';
import { ReadAloudButton } from './Message.ReadAloud';
import { ShareButton } from './Message.ShareChatThread';
import { RelatedQuestionsPane } from './QueryHints';
import { QueryMetadataPane } from './QueryMetadata';
type Props = {
  item: Chat.Query;
  numCitations: number;
};

export const SystemUserMessageFooter = ({ item, numCitations }: Props) => {
  const chat = useContext(ActiveChatSessionContext);
  const chatPermissions = useChatPermissions();
  const capabilities = useContext(CapabilitiesContext);
  const [citationsOpen, toggleCitationsOpen] = useContext(MessageCitationsToggleContext);

  const { copyChatMessages, shareChats, exportChatData, followUps } = useGroupFeatures();

  const { mutateAsync: updateQueryRating } = useUpdateQueryRatingMutation({
    chatInstanceId: chat.id,
    queryIdentifier: item.identifier,
  });

  const openFeedbackModal = useContext(OpenQueryFeedbackModalContext);

  const copyToClipboard = useCopyToClipboard();

  const [toggleShareModel, ShareModal] = useShareChatModal();

  const handleShareChatThread = useCallback(() => {
    toggleShareModel();
  }, [toggleShareModel]);

  const handleRatingClicked = useCallback(
    (value: Chat.Rating) => {
      if (value <= 3) {
        return openFeedbackModal({
          queryIdentifier: item.identifier,
          chatInstanceId: chat.id,
          rating: value,
        });
      }

      return updateQueryRating(value);
    },
    [item.identifier, updateQueryRating, openFeedbackModal, chat.id]
  );

  const apiClient = useChatApiClient();

  const getResponseText = useCallback(async () => {
    return apiClient.queries.getQueryResponseText({
      chatInstanceId: chat.id,
      queryId: item.id,
      tts: false,
    });
  }, [apiClient, chat.id]);

  const handleCopy = useCallback(() => {
    getResponseText().then((text) => {
      if (text) {
        copyToClipboard(text);
      }
    });
  }, [copyToClipboard, getResponseText]);

  const view = {
    rating: chatPermissions.canModify,
    followUps: followUps && chatPermissions.canModify && !!item.suggestedFollowups.length,
    metadata: capabilities.canViewMetadata && !!item.metadata,
    trace: capabilities.canViewTraces && !!item.langfuseTraceUrl,
  };

  return (
    <>
      <Layer2>
        <ActionsContainer>
          <ActionsLeft>
            {shareChats && <ShareButton onClick={handleShareChatThread} />}
            <ShareModal chatInstance={chat} />

            <MessageButtons item={item} />
            {copyChatMessages && <CopyButton onClick={handleCopy} />}

            {exportChatData &&
              [ChatResponseType.BIChartQuantAnalyses, ChatResponseType.Chart].includes(
                item.response?.type
              ) && <DownloadChartsButton />}

            <ReadAloudButton queryId={item.id} />
          </ActionsLeft>

          <ActionsRight>
            {view.rating && <StarRating value={item.rating} onChange={handleRatingClicked} />}
            {view.trace && <GoToTraceUrl url={item.langfuseTraceUrl} />}
          </ActionsRight>
        </ActionsContainer>
      </Layer2>
      {!!numCitations && (
        <StyledCitationsPane
          title={`Sources (${item.citations.reduce((total, citation) => {
            const amt = citation.snippets.length > 0 ? citation.snippets.length : 1;
            return total + amt;
          }, 0)})`}
          open={citationsOpen}
          toggleOpen={toggleCitationsOpen}
        >
          <StyledMessageCitations items={item.citations} />
        </StyledCitationsPane>
      )}
      {view.followUps && item.suggestedFollowups.length > 0 && (
        <StyledRelatedQuestions
          profileKey="general-query"
          queryIdentifier={item.identifier}
          hints={item.suggestedFollowups.map((f) => ({
            displayValue: f,
            type:
              item.response.type === ChatResponseType.BIChartQuantAnalyses
                ? ChatHintType.Chart
                : ChatHintType.Text,
          }))}
        />
      )}
      {view.metadata &&
        (item.metadata.rewrittenQuery || item.metadata.evaluation || item.metadata.classifications) && (
          <StyledMetadata metadata={item.metadata} />
        )}
    </>
  );
};

type ContainerProps = {
  children: React.ReactNode;
  className?: string;
};

export const SystemUserMessageFooterContainer = memo(({ className, children }: ContainerProps) => {
  return (
    <Root className={className}>
      <Wrap>{children}</Wrap>
    </Root>
  );
});

const Root = styled.div`
  margin-top: 15px;
`;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledRelatedQuestions = styled(RelatedQuestionsPane)`
  margin-top: 15px;
`;

const StyledCitationsPane = styled(ChatResponsePane)`
  margin-top: 15px;
`;

const StyledMessageCitations = styled(MessageCitations)`
  margin-top: 10px;
`;

const StyledMetadata = styled(QueryMetadataPane)`
  margin-top: 15px;
`;

const Layer2 = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const ActionsContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  rowGap: 8,
  width: '100%',
  containerType: 'inline-size',
  containerName: 'message-actions',
});

const ActionsLeft = styled.div({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 10,
  rowGap: 8,
});

const ActionsRight = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  marginLeft: 'auto',
});
